import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, Toast } from '../../components';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { createShadow } from '../../utils/styles';
import { MainStackParamList } from '../../navigation/MainNavigator';
import api from '../../services/api';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/config';

type NavProp = NativeStackNavigationProp<MainStackParamList>;

const FOREGROUND = '#414042';
const BORDER = '#E2E8F0';
const MUTED = '#64748B';
const BRAND_RED = '#A9211F';

/* ── Icons ──────────────────────────────────────────────────── */
function BuildingIcon({ size = 16, color = MUTED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M10 6h4M10 10h4M10 14h4M10 18h4" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function MapPinIcon({ size = 16, color = MUTED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M18 8c0 4.5-6 9-6 9s-6-4.5-6-9a6 6 0 0 1 12 0" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Circle cx="12" cy="8" r="2" stroke={color} strokeWidth={1.5} />
      <Path d="M8.835 14H5a1 1 0 0 0-.9.7l-2 6c-.1.1-.1.2-.1.3 0 .6.4 1 1 1h18c.6 0 1-.4 1-1 0-.1 0-.2-.1-.3l-2-6a1 1 0 0 0-.9-.7h-3.835" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function CalendarIcon({ size = 16, color = MUTED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M16 2v4M8 2v4M3 10h5" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M17.5 17.5 16 16.3V14" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Circle cx="16" cy="16" r="6" stroke={color} strokeWidth={1.5} />
    </Svg>
  );
}

function CheckIcon({ size = 12, color = '#FFFFFF' }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M20 6L9 17l-5-5" stroke={color} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** Convert "HH:MM" 24h to "hh:MM AM/PM" */
function to12h(time24: string): string {
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr || '0', 10);
  const m = mStr || '00';
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${period}`;
}

function formatTimeRange12h(range: string): string {
  const parts = range.split('-').map(s => s.trim());
  if (parts.length === 2) return `${to12h(parts[0])} - ${to12h(parts[1])}`;
  return range;
}

interface SpecItem {
  id: number;
  name: string;
  description: string;
  location: string;
  start_date: string | null;
  end_date: string | null;
  activity_schedules: { dates: string[]; times: string[]; days: string[] }[];
}

export default function StudentTalentRegisterScreen() {
  const navigation = useNavigation<NavProp>();
  const route = useRoute<RouteProp<MainStackParamList, 'StudentTalentRegister'>>();
  // Set when a guardian opens this for a specific child; absent for a student
  // registering for themselves.
  const targetStudentId = route.params?.studentId;
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { colors, isDark } = useTheme();
  const { user } = useAuth();
  const { isMobile, isDesktop } = useResponsive();

  const [specializations, setSpecializations] = useState<SpecItem[]>([]);
  const [enrolledSpecs, setEnrolledSpecs] = useState<Record<number, string>>({});
  const [selectedSpecs, setSelectedSpecs] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [studentData, setStudentData] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // A guardian registers on behalf of a named child, so the target comes
      // from the route. Without it, resolve the caller's own student record.
      const profileResp = targetStudentId
        ? await api.get(`/talent-students/${targetStudentId}/`)
        : await api.get('/talent-students/me/');
      const profile = profileResp.data || profileResp;
      setStudentData(profile);

      const specMap: Record<number, string> = {};
      (profile.specializations || []).forEach((s: any) => {
        const status = s.link_status || 'active';
        // Don't mark rejected as enrolled — student can re-apply
        if (status !== 'rejected') {
          specMap[typeof s === 'number' ? s : s.id] = status;
        }
      });
      setEnrolledSpecs(specMap);

      // Get all published specializations from all departments
      const specsResp = await axios.get(`${API_BASE_URL}/talent-register/specializations/`);
      setSpecializations(specsResp.data || []);
    } catch (e) {
      console.log('[StudentTalentRegister] load failed:', e);
    }
    setLoading(false);
  };

  const toggleSpec = (specId: number) => {
    if (specId in enrolledSpecs) return;
    setSelectedSpecs(prev =>
      prev.includes(specId) ? prev.filter(id => id !== specId) : [...prev, specId]
    );
  };

  const handleSubmit = async () => {
    if (selectedSpecs.length === 0) {
      setToastType('error');
      setToastMsg(isRTL ? 'يرجى اختيار موهبة واحدة على الأقل' : 'Please select at least one talent');
      return;
    }
    setSubmitting(true);
    try {
      if (studentData?.id) {
        // Authenticated enrolment: ownership comes from the session. The public
        // add-specializations endpoint verifies student_id + EID, which proves
        // nothing for a minor registered without an Emirates ID.
        await api.post(`/talent-students/${studentData.id}/register-talent/`, {
          specialization_ids: selectedSpecs,
        });
      } else {
        // New registration
        await axios.post(`${API_BASE_URL}/talent-register/`, {
          name: user?.profile?.full_name || user?.profile?.username || '',
          eid: '',
          email: user?.profile?.email || '',
          phone: '',
          school: '',
          gender: '',
          department_id: studentData?.department_id || 75,
          specialization_ids: selectedSpecs,
          registration_method: 'manual',
        });
      }
      setToastType('success');
      setToastMsg(isRTL ? 'تم تسجيل الطلب بنجاح' : 'Registration submitted successfully');
      setSelectedSpecs([]);
      // Navigate back to main page after short delay
      setTimeout(() => navigation.navigate('Talents'), 1500);
    } catch (e: any) {
      const errMsg = e?.response?.data?.error || (isRTL ? 'حدث خطأ أثناء التسجيل' : 'Registration failed');
      setToastType('error');
      setToastMsg(errMsg);
    }
    setSubmitting(false);
  };

  const fmtDate = (d: string) => {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('ar-AE', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 16 : 24, paddingBottom: 40 }}>
        <Breadcrumb
          items={[
            { label: 'مبادرة مواهب', onPress: () => navigation.navigate('Talents') },
            { label: 'التسجيل في موهبة' },
          ]}
        />

        {/* Title */}
        <Text style={[styles.pageTitle, { color: colors.textPrimary }]}>أنواع المواهب المتاحة</Text>
        <Text style={[styles.pageSubtitle, { color: colors.textMuted || MUTED }]}>
          اختر موهبة واحدة أو أكثر تهمك. انقر على بطاقة الموهبة لاختيارها.
        </Text>

        {loading ? (
          <ActivityIndicator size="large" color={BRAND_RED} style={{ marginTop: 40 }} />
        ) : specializations.length === 0 ? (
          <Text style={[styles.emptyText, { color: colors.textMuted || MUTED }]}>لا توجد مواهب متاحة حالياً</Text>
        ) : (
          <>
            {/* Cards Grid */}
            <View style={[styles.cardsGrid, { flexDirection: isDesktop ? 'row-reverse' : 'column' }]}>
              {specializations.map((spec) => {
                const isEnrolled = spec.id in enrolledSpecs;
                const linkStatus = enrolledSpecs[spec.id];
                const isSelected = selectedSpecs.includes(spec.id);
                const GOLD = '#A6813F';
                const enrolledColor = linkStatus === 'active' ? '#22C55E' : linkStatus === 'proposed' ? '#3B82F6' : '#F59E0B';
                const borderColor = isEnrolled ? enrolledColor : isSelected ? GOLD : BORDER;
                const borderWidth = isEnrolled || isSelected ? 2 : 1;

                // Badge config per status
                const badgeConfig: Record<string, { bg: string; color: string; text: string }> = {
                  applied: { bg: '#FFEDD5', color: '#9A3412', text: 'بإنتظار الموافقة' },
                  proposed: { bg: '#DBEAFE', color: '#1E40AF', text: 'بإنتظار الاعتماد' },
                  active: { bg: '#DCFCE7', color: '#16A34A', text: 'نشط' },
                  rejected: { bg: '#FEE2E2', color: '#DC2626', text: 'مرفوض' },
                };
                const badge = isEnrolled ? badgeConfig[linkStatus] || badgeConfig.active : null;

                return (
                  <TouchableOpacity
                    key={spec.id}
                    style={[
                      styles.card,
                      {
                        backgroundColor: isDark ? colors.card : '#FFFFFF',
                        borderColor,
                        borderWidth,
                      },
                      isDesktop && { width: '48%' },
                      isEnrolled && { opacity: 0.85 },
                    ]}
                    onPress={() => toggleSpec(spec.id)}
                    activeOpacity={isEnrolled ? 1 : 0.7}
                  >
                    {/* Header: title + badge/checkbox */}
                    <View style={styles.cardHeader}>
                      <Text style={[styles.cardName, { color: colors.textPrimary }]}>{spec.name}</Text>
                      {isEnrolled && badge ? (
                        <View style={[styles.activeBadge, { backgroundColor: badge.bg }]}>
                          <Text style={[styles.activeBadgeText, { color: badge.color }]}>{badge.text}</Text>
                        </View>
                      ) : isSelected ? (
                        <View style={[styles.checkbox, { backgroundColor: GOLD, borderColor: GOLD }]}>
                          <CheckIcon size={12} color="#FFFFFF" />
                        </View>
                      ) : (
                        <View style={styles.checkbox} />
                      )}
                    </View>

                    {/* Description */}
                    {spec.description ? (
                      <Text style={[styles.cardDesc, { color: colors.textPrimary }]}>{spec.description}</Text>
                    ) : null}

                    {/* Metadata */}
                    <View style={styles.meta}>
                      <View style={[styles.metaRow, { borderColor: isEnrolled ? '#86EFAC' : isSelected ? FOREGROUND : BORDER }]}>
                        <BuildingIcon size={16} color={isEnrolled ? '#16A34A' : MUTED} />
                        <Text style={[styles.metaText, { color: isEnrolled ? '#16A34A' : MUTED }]}>{spec.location || '--'}</Text>
                      </View>
                      <View style={[styles.metaRow, { borderColor: isEnrolled ? '#86EFAC' : isSelected ? FOREGROUND : BORDER }]}>
                        <MapPinIcon size={16} color={isEnrolled ? '#16A34A' : MUTED} />
                        <Text style={[styles.metaText, { color: isEnrolled ? '#16A34A' : MUTED }]}>{spec.location ? `الفجيرة - ${spec.location}` : '--'}</Text>
                      </View>
                      <View style={[styles.metaRow, { borderColor: isEnrolled ? '#86EFAC' : isSelected ? FOREGROUND : BORDER, alignItems: 'flex-start' }]}>
                        <CalendarIcon size={16} color={isEnrolled ? '#16A34A' : MUTED} />
                        <View style={{ flex: 1, gap: 4 }}>
                          {spec.start_date && spec.end_date ? (
                            <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 4 }}>
                              <View style={styles.pill}><Text style={styles.pillText}>{fmtDate(spec.start_date)}</Text></View>
                              <View style={styles.pill}><Text style={styles.pillText}>{fmtDate(spec.end_date)}</Text></View>
                            </View>
                          ) : (
                            <Text style={[styles.metaText, { color: MUTED }]}>--</Text>
                          )}
                          {spec.activity_schedules?.length > 0 && (() => {
                            const allTimes = spec.activity_schedules.flatMap(a => a.times);
                            const uniqueTimes = [...new Set(allTimes)];
                            const allDays = spec.activity_schedules.flatMap(a => a.days);
                            const uniqueDays = [...new Set(allDays)];
                            return (
                              <>
                                {uniqueTimes.length > 0 && (
                                  <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 4 }}>
                                    {uniqueTimes.map((t: string) => (
                                      <View key={t} style={styles.pill}><Text style={styles.pillText}>{formatTimeRange12h(t)}</Text></View>
                                    ))}
                                  </View>
                                )}
                                {uniqueDays.length > 0 && (
                                  <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 4 }}>
                                    {uniqueDays.map((day: string) => (
                                      <View key={day} style={styles.pill}><Text style={styles.pillText}>{day}</Text></View>
                                    ))}
                                  </View>
                                )}
                              </>
                            );
                          })()}
                        </View>
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>

            {/* Selected summary */}
            <View style={[styles.summaryBar, selectedSpecs.length === 0 && { backgroundColor: '#FDDCAB', borderRightColor: '#F59E0B' }]}>
              <Text style={styles.summaryTitle}>{`انواع المواهب المختارة (${selectedSpecs.length})`}</Text>
              {specializations
                .filter(s => selectedSpecs.includes(s.id))
                .map(s => (
                  <Text key={s.id} style={styles.summaryItem}>{s.name}</Text>
                ))}
            </View>

            {/* Submit */}
            <TouchableOpacity
              style={[styles.submitBtn, submitting && { opacity: 0.6 }]}
              onPress={handleSubmit}
              disabled={submitting}
            >
              <Text style={styles.submitText}>
                {submitting ? '...' : 'إرسال طلب التسجيل'}
              </Text>
            </TouchableOpacity>
          </>
        )}
      </ScrollView>

      <Toast
        message={toastMsg}
        visible={!!toastMsg}
        type={toastType}
        onHide={() => setToastMsg('')}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  pageTitle: {
    fontFamily: FONTS.medium,
    fontSize: 22,
    textAlign: 'center',
    marginTop: 16,
  },
  pageSubtitle: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 24,
  },
  emptyText: {
    fontFamily: FONTS.regular,
    fontSize: 15,
    textAlign: 'center',
    marginTop: 40,
  },
  cardsGrid: {
    flexWrap: 'wrap',
    gap: 20,
    justifyContent: 'flex-start',
  },
  card: {
    borderRadius: 8,
    padding: 20,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 10,
  },
  cardName: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    flex: 1,
    textAlign: 'right',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: '#CBD5E1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeBadge: {
    backgroundColor: '#DCFCE7',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  activeBadgeText: {
    fontFamily: FONTS.medium,
    fontSize: 12,
    color: '#16A34A',
  },
  cardDesc: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    textAlign: 'right',
    marginTop: 8,
    lineHeight: 16,
  },
  meta: {
    marginTop: 8,
    gap: 8,
  },
  metaRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderRadius: 4,
    padding: 8,
  },
  metaText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    flex: 1,
    textAlign: 'right',
  },
  pill: {
    backgroundColor: '#E2E8F0',
    borderRadius: 2,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  pillText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: FOREGROUND,
    textAlign: 'center',
  },
  summaryBar: {
    backgroundColor: '#DCFCE7',
    borderRadius: 4,
    borderRightWidth: 4,
    borderRightColor: '#22C55E',
    padding: 16,
    paddingHorizontal: 24,
    marginTop: 24,
    alignItems: 'flex-end',
    gap: 4,
  },
  summaryTitle: {
    fontFamily: FONTS.medium,
    fontSize: 16,
    color: FOREGROUND,
    textAlign: 'right',
  },
  summaryItem: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: FOREGROUND,
    textAlign: 'right',
  },
  submitBtn: {
    backgroundColor: BRAND_RED,
    borderRadius: 8,
    paddingVertical: 14,
    marginTop: 24,
    alignItems: 'center',
  },
  submitText: {
    fontFamily: FONTS.medium,
    fontSize: 16,
    color: '#FFFFFF',
  },
});
